import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { ITeamMember } from '../../../types/teamMember';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { getTeamMembersAsync } from './getTeamMembers';

interface IUpdateTeamMemberRequest {
  teamMemberId: string;
  role: string;
  outlets: string[];
}

interface IUpdateTeamMemberResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    role?: {
      id?: string;
      name?: string;
      slug?: string;
      description?: string;
      isDefault?: boolean;
    };
    outlets: {
      id?: string;
      name?: string;
      address?: string;
      city?: string;
      state?: string;
      isActive?: boolean;
      createdAt?: string;
      updatedAt?: string;
    }[];
    inviteStatus?: ITeamMember['inviteStatus'];
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IUpdateTeamMemberResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const updateTeamMemberAsync = createAsyncThunk(
  'teamMember/update',
  async (
    values: IUpdateTeamMemberRequest,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IUpdateTeamMemberResponse>(
        `/team-members/${values.teamMemberId}`,
        values,
        config
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Team member updated successfully',
        })
      );

      dispatch(resetTeamMemberUpdate());

      dispatch(getTeamMembersAsync());

      return data;
    } catch (err) {
      const error = err as AxiosError<IUpdateTeamMemberResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(
        showAlert({
          status: 'success',
          description: errorMessage,
        })
      );

      return rejectWithValue(error.response?.data);
    }
  }
);

export const teamMemberUpdateSlice = createSlice({
  name: 'teamMember/update',
  initialState,
  reducers: {
    resetTeamMemberUpdate(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateTeamMemberAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(updateTeamMemberAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IUpdateTeamMemberResponse;
      })
      .addCase(updateTeamMemberAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IUpdateTeamMemberResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetTeamMemberUpdate } = teamMemberUpdateSlice.actions;

export default teamMemberUpdateSlice.reducer;
